/**
 * Этажи отчёта — единственный источник правды об их составе.
 *
 * Один и тот же этаж описывается в трёх местах: чипом в навигации,
 * заголовком над блоком и подписью кнопки «Подробнее в …» из резюме.
 * Держать это тремя списками — гарантированный расход: переименовали
 * раздел в одном месте, забыли в двух.
 *
 * `description` — объяснение смысла блока. Раньше оно пряталось за
 * значком вопроса; теперь стоит текстом под заголовком, до контента:
 * читателю не должно приходиться наводить мышь, чтобы понять, что он
 * сейчас смотрит.
 *
 * @format
 */

import {SECTION_IDS, type JourneyStage, type SectionId} from './types';

/** Неразрывный пробел: escape-последовательность, а не живой символ — его стирает eslint --fix. */
const NBSP = '\u00A0';

export interface Floor {
  id: SectionId;
  /** Короткая подпись для чипа навигации. */
  navLabel: string;
  /** Заголовок над блоком. Пусто — блок идёт без заголовка. */
  title?: string;
  /** Объяснение смысла блока, под заголовком и перед контентом. */
  description?: string;
  stage: JourneyStage;
}

export const FLOORS: Floor[] = [
  {
    id: SECTION_IDS.summary,
    navLabel: 'Главное',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.score,
    navLabel: 'Оценка',
    title: 'Оценка кабинета',
    description:
      'Мы сравниваем ваш кабинет с тысячами других: сколько ошибок обычно бывает при таком бюджете, как быстро их устраняют, насколько регулярно ведут кампании. Под баллом — из чего он сложился.',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.risks,
    navLabel: 'Деньги под риском',
    title: 'Деньги под риском',
    description:
      `Деньги под${NBSP}риском${NBSP}— это объём денег, который тратит рекламная кампания с${NBSP}активным критическим инцидентом. Критический инцидент мог спровоцировать либо${NBSP}прямые убытки, либо${NBSP}риски неэффективной траты${NBSP}средств.`,
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.zeroResult,
    navLabel: 'Расход вхолостую',
    title: `Расход без${NBSP}обращений`,
    description: `Кампании, которые тратили бюджет весь период и${NBSP}не принесли ни одного${NBSP}обращения. Клики есть, результата нет${NBSP}— значит деньги ушли на${NBSP}трафик, который не превращается в${NBSP}заявки.`,
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.campaigns,
    navLabel: 'Кампании',
    title: 'Светофор кампаний',
    description:
      'Все кампании кабинета, разложенные по одному признаку: что с ними делать. Красные требуют вашего вопроса команде, жёлтые — контроля, зелёные работают в плане и их стоит масштабировать.',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.clients,
    navLabel: 'Клиенты',
    title: 'Клиенты: план и факт',
    description:
      'Сколько обращений вы должны были получить на израсходованный бюджет и сколько получили на самом деле. Разница — это клиенты, которые не пришли из⁠-⁠за того, что обращение стоит дороже плана.',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.team,
    navLabel: 'Работа команды',
    title: 'Как команда работает с кабинетом',
    description:
      'Кампанию мало запустить — её нужно вести: чистить запросы и площадки, править ставки и тексты. Здесь видно, сколько дней такая работа велась на самом деле и когда кабинет оставался без внимания.',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.incidents,
    navLabel: 'Найденные ошибки',
    title: 'Что нашла система за период',
    description:
      'Каждый день 110 автоматических проверок обходят кабинет. Здесь — сколько ошибок они нашли и сколько из них уже закрыто. Разбор каждой ошибки живёт в самом AdSensor.',
    stage: 'diagnosis',
  },
  {
    id: SECTION_IDS.questions,
    navLabel: 'Вопросы команде',
    title: 'Вопросы вашей команде',
    description:
      'Готовые формулировки для планёрки. Разбираться в настройках не нужно — достаточно задать вопрос и оценить, насколько внятным будет ответ.',
    stage: 'action',
  },
  {
    id: SECTION_IDS.tasks,
    navLabel: 'Задачи',
    title: 'Задачи специалистам',
    description:
      'То же самое, но на языке исполнителя: что именно сделать, в каких кампаниях и в какой срок. Список можно скопировать целиком и отдать агентству или штатной команде.',
    stage: 'action',
  },
  {
    id: SECTION_IDS.control,
    navLabel: 'Контроль',
    title: 'Следующая точка контроля',
    // Без пояснения: дата стоит прямо в заголовке и объясняет этаж сама,
    // а довод «почему точка вообще нужна» развёрнут внутри блока.
    stage: 'control',
  },
  {
    id: SECTION_IDS.notifications,
    navLabel: 'Не пропустить',
    title: 'Как не пропустить следующий отчёт',
    description:
      'Отчёт полезен, пока за ним следят. Выберите, куда вам написать о контрольной точке и о новых критичных ошибках, — иначе о них станет известно только через месяц.',
    stage: 'cta',
  },
];

/** Подпись этажа для ссылок вида «Подробнее в …». */
export function floorLabel(id: SectionId): string {
  return FLOORS.find(f => f.id === id)?.navLabel ?? '';
}
